import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import { Box, TextField, Button, Typography, Link, CircularProgress } from "@mui/material";
import client from "../../api/client";
import AuthShell from "./AuthShell";

/**
 * Step 1 of password reset: request a 6-digit OTP by email. The response is
 * intentionally generic (no account enumeration), so we always advance to the
 * reset step with the entered email.
 */
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Enter your email");
      return;
    }
    setSubmitting(true);
    const cleanEmail = email.trim().toLowerCase();
    try {
      await client.post("/auth/forgot-password", { email: cleanEmail });
      toast.success("If that email is registered, a reset code has been sent");
      navigate("/reset-password", { state: { email: cleanEmail } });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not send the reset code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "secondary.main" }}>
          Forgot password
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Enter your account email and we'll send a reset code
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          autoFocus
          autoComplete="email"
          sx={{ mb: 2 }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ py: 1.25 }}
        >
          {submitting ? "Sending…" : "Send reset code"}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ textAlign: "center", mt: 3, color: "text.secondary" }}>
        Remembered it?{" "}
        <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
          Back to sign in
        </Link>
      </Typography>
    </AuthShell>
  );
};

export default ForgotPasswordPage;
